import React from 'react';
import {observer} from 'mobx-react';
import s from '../../s';
import ModalSimple from '../../../global/modal-simple';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Keyboard, Scrollbar, Navigation, Zoom} from 'swiper/modules';
import ProductRender from '../../rendering/render';
import {ZOOM_MODAL_ID, ZOOM_SWIPER_ID, pauseVideosIn, pickZoomVideoSrc, shouldLoopZoom, webpSrc, type ProductSlide, type ProductSlideZoom} from './product-slides/product-slides';

declare const window: any;

interface SliderTHModalProps {
    slides: ProductSlide[];
    onSwiperInit: (swiper: any) => void;
    onClose: () => void;
}

const zoomBoxStyle = (): React.CSSProperties => ({
    maxWidth: window.innerWidth * 0.7,
    maxHeight: window.innerHeight * 0.7,
});

const ZoomImage: React.FC<{zoom: Extract<ProductSlideZoom, {type: 'image'}>}> = ({zoom}) => (
    <>
        {zoom.overlay ? <div className="zoom-overlay">{zoom.overlay}</div> : null}
        <picture className="swiper-zoom-container">
            <source
                type="image/webp"
                srcSet={webpSrc(zoom.src)}
            />
            <img
                src={zoom.src}
                alt={zoom.alt}
                style={{
                    ...zoomBoxStyle(),
                    backgroundColor: zoom.backgroundColor,
                }}
            />
        </picture>
    </>
);

const ZoomBody: React.FC<{slide: ProductSlide}> = ({slide}) => {
    if (slide.zoom.type === 'product-render') {
        return (
            <ProductRender
                zoom={true}
                pcs={s.pcs}
                css={s.ps.css_zoom}
            />
        );
    }

    if (slide.zoom.type === 'video') {
        return (
            <video
                className="slide-video-modal-video"
                controls
                playsInline
                preload="metadata"
                poster={slide.zoom.poster}
                style={zoomBoxStyle()}
            >
                <source
                    src={pickZoomVideoSrc(slide.zoom, window.innerWidth, window.innerHeight)}
                    type="video/mp4"
                />
            </video>
        );
    }

    return <ZoomImage zoom={slide.zoom} />;
};

const SliderTHModal: React.FC<SliderTHModalProps> = observer(({slides, onSwiperInit, onClose}) => {
    if (!s.ui.zoom_thumbnails || slides.length < 1) {
        return null;
    }

    const loop = shouldLoopZoom(slides.length);

    return (
        <ModalSimple
            id={ZOOM_MODAL_ID}
            title="Product Zoom"
            open={true}
            maxWidth={'90%'}
            close={onClose}
        >
            <Swiper
                id={ZOOM_SWIPER_ID}
                onInit={onSwiperInit}
                onSlideChange={() => {
                    pauseVideosIn(document.getElementById(ZOOM_SWIPER_ID));
                }}
                slidesPerView={1}
                centeredSlides={true}
                grabCursor={true}
                zoom={true}
                keyboard={{enabled: true}}
                loop={loop}
                scrollbar={true}
                navigation={loop}
                pagination={false}
                modules={[Keyboard, Scrollbar, Navigation, Zoom]}
                slidesPerGroupAuto={true}
                aria-roledescription="carousel"
                role="region"
                aria-label="Product images"
            >
                {slides.map(slide => (
                    <SwiperSlide
                        key={slide.id}
                        id={slide.id}
                    >
                        <ZoomBody slide={slide} />
                    </SwiperSlide>
                ))}
            </Swiper>
        </ModalSimple>
    );
});

export default SliderTHModal;
